'use client'

/**
 * MiniBrowserFrame — the four-tier render engine behind the askFinz mini-browser.
 *
 * This is the load-bearing half of the mini-browser, lifted out of
 * `search.askfinz.ai/app/components/SearchView.tsx` so every askFinz app can embed a live web page
 * the same way. The tab bar, URL bar, panes and profiles are chrome around this; the tier choice
 * below is what actually decides whether a page appears at all.
 *
 * THE FOUR TIERS, in the order they are tried:
 *
 *   1. Electron Desktop  → native <webview>. The host owns the session outright.
 *   2. askFinz extension → direct <iframe> to the real origin. The extension's
 *      declarativeNetRequest rules REMOVE X-Frame-Options / Frame-Options / CSP on SUB_FRAME
 *      requests, so a site that blocks framing embeds anyway. The user's own browser fetches and
 *      paints it: their IP, their cookies, working SPAs, zero server cost.
 *   3. No extension, frame-friendly host → direct <iframe>, gated on an `/api/frame-check`
 *      preflight that reads the framing headers first. Fails CLOSED to tier 4.
 *   4. Everything else → server proxy `/api/browse-proxy`, which strips the headers on our side.
 *      Costs a server fetch and carries no user cookies, so it is genuinely the last resort.
 *
 * WHY `sandbox` IS NOT APPLIED BY DEFAULT — measured 2026-08-04 in real Chrome, not headless:
 * the page inspector on askfinz.ai carried `sandbox="allow-scripts allow-same-origin ..."`, and on
 * a PDF that produced a broken-file icon and a contentDocument that threw. Chrome disables the
 * built-in PDF viewer inside a sandboxed frame. Removing `sandbox` made the viewer appear; loading
 * the ORIGIN url directly (tier 2) rendered all 11 pages correctly. A headless run could not have
 * shown this — `chrome-headless-shell` ships no PDF viewer, so both arms of the test looked
 * identically empty. Callers that genuinely need the sandbox can still opt in via `sandbox`.
 *
 * SAME-ORIGIN READABILITY is the one thing tiers disagree on, and callers must know which they
 * got. Tier 4 serves the page from our own origin, so `contentDocument` is readable and features
 * like element highlighting work. Tiers 1–3 render the real origin, so it is NOT readable. A
 * caller that depends on reading the document (the indexing inspector's "Inspect elements" mode)
 * should pass `forceProxy` while that mode is active, and drop it again afterwards — that keeps
 * accurate rendering by default without silently breaking inspection.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export type FrameTier = 'desktop' | 'extension' | 'direct' | 'proxy' | 'checking'

export interface MiniBrowserFrameProps {
  /** The real page to show. Always the ORIGIN url — proxying is applied internally when needed. */
  url: string
  /** Bump to force a reload without changing the url. */
  refreshKey?: number | string
  className?: string
  style?: React.CSSProperties
  title?: string
  /**
   * Pin to the server proxy regardless of tier. Use when the caller must READ the rendered
   * document (same-origin), e.g. drawing overlays on elements. Costs fidelity — see the note above.
   */
  forceProxy?: boolean
  /** Ask the proxy for a headless pre-render (JS-built pages that arrive blank through the proxy). */
  proxyRender?: boolean
  /** Applied verbatim when set. Left off by default because it breaks the PDF viewer. */
  sandbox?: string
  /** Where this app serves the two helper routes. Both already exist on askfinz.ai and search. */
  proxyPath?: string
  frameCheckPath?: string
  onTierChange?: (tier: FrameTier) => void
  onLoad?: (info: { tier: FrameTier; sameOriginReadable: boolean }) => void
  /**
   * Attached to the underlying <iframe> on tiers 2–4 and left null on Electron (a <webview> is not
   * an iframe). Callers that read `contentDocument` — element overlays, blank-page detection —
   * need this, and must still guard the read: only tier 'proxy' is same-origin.
   */
  frameRef?: React.RefObject<HTMLIFrameElement | null>
  /** Rendered instead of the frame while the tier-3 preflight is still in flight. */
  placeholder?: React.ReactNode
}

/** True once the askFinz extension has announced itself. Cheap to call repeatedly. */
export function useExtensionDetected(): boolean {
  const [detected, setDetected] = useState(false)
  useEffect(() => {
    // inject.js sets this at document_start in the MAIN world; bridge_relay.js also postMessages
    // an id. Poll briefly rather than assume, because the flag can land after our first paint.
    let tries = 0
    const check = () => {
      const w = window as any
      if (w.__ASKFINZ_EXTENSION_ACTIVE__ || w.askfinzExtension) {
        setDetected(true)
        return true
      }
      return false
    }
    if (check()) return
    const t = setInterval(() => {
      if (check() || ++tries > 20) clearInterval(t)
    }, 150)
    const onMsg = (e: MessageEvent) => {
      if (e?.data && (e.data as any).__askfinz_ext_id) setDetected(true)
    }
    window.addEventListener('message', onMsg)
    return () => {
      clearInterval(t)
      window.removeEventListener('message', onMsg)
    }
  }, [])
  return detected
}

/** True when running inside the Electron desktop shell, where a native <webview> is available. */
export function useIsDesktop(): boolean {
  const [d, setD] = useState(false)
  useEffect(() => {
    const w = window as any
    setD(!!(w.askfinzDesktop || w.electronAPI || /askFinzDesktop/i.test(navigator.userAgent)))
  }, [])
  return d
}

/**
 * Per-host framing verdict for tier 3. Cached in sessionStorage because the answer is a property
 * of the host, not of the tab, and re-asking on every navigation wastes a round trip.
 *
 * FAILS CLOSED: anything other than a clear "framable" answer resolves to 'proxy'. A false
 * 'direct' shows the user an empty frame; a false 'proxy' merely costs a server fetch.
 */
export function useFrameCheck(
  url: string,
  enabled: boolean,
  frameCheckPath = '/api/frame-check',
): 'direct' | 'proxy' | undefined {
  const [modes, setModes] = useState<Record<string, 'direct' | 'proxy'>>({})
  const inFlight = useRef<Set<string>>(new Set())
  const host = useMemo(() => {
    try {
      return new URL(url).hostname
    } catch {
      return ''
    }
  }, [url])

  useEffect(() => {
    if (!enabled || !host || modes[host] || inFlight.current.has(host)) return
    try {
      const cached = sessionStorage.getItem(`afz_fc_${host}`)
      if (cached === 'direct' || cached === 'proxy') {
        setModes((p) => ({ ...p, [host]: cached }))
        return
      }
    } catch {
      /* private mode — just ask again */
    }
    inFlight.current.add(host)
    ;(async () => {
      let mode: 'direct' | 'proxy' = 'proxy'
      try {
        const r = await fetch(`${frameCheckPath}?url=${encodeURIComponent(url)}`)
        if (r.ok) {
          const j = await r.json()
          mode = j?.framable ? 'direct' : 'proxy'
        }
      } catch {
        mode = 'proxy'
      }
      try {
        sessionStorage.setItem(`afz_fc_${host}`, mode)
      } catch {
        /* ignore */
      }
      setModes((p) => ({ ...p, [host]: mode }))
      inFlight.current.delete(host)
    })()
  }, [enabled, host, url, modes, frameCheckPath])

  return enabled ? modes[host] : undefined
}

/** Decides the tier without rendering anything — useful for callers that need to branch first. */
export function useFrameTier(
  url: string,
  opts: { forceProxy?: boolean; frameCheckPath?: string } = {},
): FrameTier {
  const isDesktop = useIsDesktop()
  const extension = useExtensionDetected()
  // The preflight is only worth running when no higher tier already applies.
  const needCheck = !opts.forceProxy && !isDesktop && !extension
  const mode = useFrameCheck(url, needCheck, opts.frameCheckPath)

  if (opts.forceProxy) return 'proxy'
  if (isDesktop) return 'desktop'
  if (extension) return 'extension'
  if (mode === 'direct') return 'direct'
  if (mode === undefined) return 'checking'
  return 'proxy'
}

export function MiniBrowserFrame({
  url,
  refreshKey = 0,
  className = '',
  style,
  title = 'Live page',
  forceProxy = false,
  proxyRender = false,
  sandbox,
  proxyPath = '/api/browse-proxy',
  frameCheckPath = '/api/frame-check',
  onTierChange,
  onLoad,
  placeholder,
  frameRef,
}: MiniBrowserFrameProps) {
  const tier = useFrameTier(url, { forceProxy, frameCheckPath })
  const lastTier = useRef<FrameTier | null>(null)

  useEffect(() => {
    if (tier !== lastTier.current) {
      lastTier.current = tier
      onTierChange?.(tier)
    }
  }, [tier, onTierChange])

  const handleLoad = useCallback(() => {
    onLoad?.({ tier, sameOriginReadable: tier === 'proxy' })
  }, [tier, onLoad])

  if (tier === 'checking') {
    return (
      <>
        {placeholder ?? (
          <div className={className} style={style} aria-busy="true" />
        )}
      </>
    )
  }

  // Tier 1 — Electron. `webview` is not in React's JSX types, hence the cast.
  if (tier === 'desktop') {
    const WebView = 'webview' as unknown as React.ElementType
    return (
      <WebView
        key={`wv-${refreshKey}`}
        src={url}
        className={className}
        style={style}
        // eslint-disable-next-line react/no-unknown-property
        allowpopups="true"
        onLoad={handleLoad}
      />
    )
  }

  const src =
    tier === 'proxy'
      ? `${proxyPath}?url=${encodeURIComponent(url)}${proxyRender ? '&render=1' : ''}`
      : url

  return (
    <iframe
      key={`${tier}-${refreshKey}-${url}`}
      ref={frameRef as React.RefObject<HTMLIFrameElement>}
      src={src}
      title={title}
      className={className}
      style={style}
      // Deliberately omitted unless the caller asks: `sandbox` disables Chrome's PDF viewer, which
      // is what made the indexing inspector show a broken-file icon on every PDF.
      {...(sandbox ? { sandbox } : {})}
      referrerPolicy="no-referrer-when-downgrade"
      allow="autoplay; encrypted-media; fullscreen; clipboard-write; picture-in-picture"
      onLoad={handleLoad}
    />
  )
}

export default MiniBrowserFrame
